import { format } from "date-fns";
import type { TFunction } from "i18next";
import { ExportTable } from "./table";
import {
  fixedDigitsNumFmtId,
  PivotDateGroup,
  PivotLayout,
  SheetSpec,
  WorkbookCell,
  WorkbookImage,
  WorkbookRow,
} from "@/utilities/xlsx";

export interface Report {
  /** Already translated. */
  title: string;
  /** Either bound may be open; callers set at least one. */
  period?: { from?: Date; to?: Date };
  /** Already formatted, e.g. a group or vehicle name. */
  filter?: string;
  extra?: [label: string, value: WorkbookCell][];
  table: ExportTable;
  extraSheets?: { name: string; table: ExportTable }[];
  /** A pivot table over the main table, on a sheet of its own after the extra sheets. */
  pivot?: ReportPivot;
  /** Further sheets worked out from the table as exported, added last. */
  appendSheets?: (table: ExportTable) => SheetSpec[];
}

/**
 * A pivot table over the report's table, for readers who analyse the data further. Fields are
 * named by their grid field; one the export leaves out (a hidden column) is left out here too.
 * Without rows and values, every field is offered and none placed.
 */
export interface ReportPivot {
  /** Already translated. */
  sheetName: string;
  /** A line above the pivot table, e.g. how to refresh it. Already translated. */
  note?: string;
  /** Outermost first. A date field may be grouped by month or day, see {@link PivotDateGroup}. */
  rows?: ReportPivotRow[];
  values?: { field: string; name: string; fractionDigits: number }[];
}

export type ReportPivotRow =
  | { field: string; collapsed?: boolean }
  | { field: string; groupBy: PivotDateGroup; name: string; collapsed?: boolean };

export interface ReportContext {
  database: string;
  userName: string;
  exportedAt: Date;
  t: TFunction;
}

const FILE_NAME_FORBIDDEN = /[\\/:*?"<>|]/g;
const FILE_DATE_FORMAT = "yyyy-MM-dd";
/**
 * The logo sits at the last table column but never further left than column D, so it does not
 * cover the preamble's title, labels and period bounds in columns A-C.
 */
const MIN_LOGO_COLUMN = 3;

const tableRows = ({ columns, rows }: ExportTable): WorkbookRow[] => [
  columns.map(column => column.header),
  ...rows,
];

export function buildReportSheets(
  report: Report,
  { database, userName, exportedAt, t }: ReportContext,
  logo?: WorkbookImage,
): SheetSpec[] {
  const preamble: WorkbookRow[] = [
    [report.title],
    [t("export.preamble.database"), database],
    ...(report.period
      ? [[t("export.preamble.period"), report.period.from ?? "", report.period.to ?? ""]]
      : []),
    ...(report.filter ? [[t("export.preamble.filter"), report.filter]] : []),
    [t("export.preamble.exportedAt"), exportedAt],
    [t("export.preamble.exportedBy"), userName],
    ...(report.extra ?? []).map(([label, value]): WorkbookRow => [label, value]),
    [],
  ];

  const main: SheetSpec = {
    name: report.title,
    rows: [...preamble, ...tableRows(report.table)],
    headerRow: preamble.length + 1,
    fractionDigits: report.table.columns.map(column => column.fractionDigits),
    ...(logo && {
      image: { ...logo, anchorColumn: Math.max(MIN_LOGO_COLUMN, report.table.columns.length - 1) },
    }),
  };

  const extras = (report.extraSheets ?? []).map(
    ({ name, table }): SheetSpec => ({
      name,
      rows: tableRows(table),
      headerRow: 1,
      fractionDigits: table.columns.map(column => column.fractionDigits),
    }),
  );

  const pivot: SheetSpec[] =
    report.pivot && report.table.columns.length > 0
      ? [
          {
            name: report.pivot.sheetName,
            // Row 1; the pivot table starts in row 3.
            rows: report.pivot.note ? [[report.pivot.note]] : [],
            pivotTable: {
              sourceSheet: 0,
              headerRow: main.headerRow ?? 1,
              lastRow: main.rows.length,
              fieldNames: report.table.columns.map(column => column.header),
              rows: report.table.rows,
              layout: pivotLayout(report.pivot, report.table),
            },
          },
        ]
      : [];

  return [main, ...extras, ...pivot, ...(report.appendSheets?.(report.table) ?? [])];
}

/** The pivot's fields as columns of the table; fields the table does not hold are dropped. */
function pivotLayout(pivot: ReportPivot, table: ExportTable): PivotLayout {
  const columnOf = (field: string) => table.columns.findIndex(column => column.field === field);
  return {
    rowFields: (pivot.rows ?? []).flatMap(row => {
      const column = columnOf(row.field);
      if (column < 0) return [];
      const collapsed = row.collapsed === true;
      return "groupBy" in row
        ? [{ column, groupBy: row.groupBy, name: row.name, collapsed }]
        : [{ column, collapsed }];
    }),
    dataFields: (pivot.values ?? []).flatMap(value => {
      const column = columnOf(value.field);
      return column < 0
        ? []
        : [{ column, name: value.name, numFmtId: fixedDigitsNumFmtId(value.fractionDigits) }];
    }),
  };
}

export function reportFileName(report: Report, { exportedAt }: ReportContext): string {
  const title = report.title.replace(FILE_NAME_FORBIDDEN, "_");
  const bounds = [report.period?.from, report.period?.to].filter(d => d !== undefined);
  const dates = (bounds.length > 0 ? bounds : [exportedAt])
    .map(d => format(d, FILE_DATE_FORMAT))
    .join("_");
  return `${title}_${dates}.xlsx`;
}
